import {
  TASK_CATEGORIES,
  TASK_COMMENTS,
  TASK_TEMPLATES,
  TASK_TYPES,
  TASKS,
} from '../../api/endpoints';
import { baseApi, listTags } from './baseApi';

const multipart =
  typeof FormData !== 'undefined'
    ? { headers: { 'Content-Type': 'multipart/form-data' } }
    : {};

const withMultipart = (payload) => {
  const isFormData =
    typeof FormData !== 'undefined' && payload instanceof FormData;
  return isFormData ? multipart : {};
};

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getTasks: build.query({
      query: (params = {}) => ({ url: TASKS.LIST, method: 'GET', params }),
      providesTags: (result) => listTags('Task', result),
    }),
    getTaskById: build.query({
      query: (arg) => {
        const id = typeof arg === 'object' && arg != null ? arg.id : arg;
        const params =
          typeof arg === 'object' && arg != null ? arg.params : undefined;
        return { url: TASKS.BY_ID(id), method: 'GET', params };
      },
      providesTags: (_r, _e, arg) => [
        { type: 'Task', id: typeof arg === 'object' && arg != null ? arg.id : arg },
      ],
    }),
    createTask: build.mutation({
      query: (arg) => {
        const isWrapped =
          arg &&
          typeof arg === 'object' &&
          !(arg instanceof FormData) &&
          ('data' in arg || 'params' in arg);
        const payload = isWrapped ? arg.data : arg;
        const params = isWrapped ? arg.params : undefined;
        return {
          url: TASKS.LIST,
          method: 'POST',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: [{ type: 'Task', id: 'LIST' }],
    }),
    updateTask: build.mutation({
      query: ({ id, data: updateData, params, ...rest }) => {
        const payload = updateData ?? rest;
        return {
          url: TASKS.BY_ID(id),
          method: 'PUT',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
      ],
    }),
    deleteTask: build.mutation({
      query: (id) => ({ url: TASKS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'Task', id },
        { type: 'Task', id: 'LIST' },
      ],
    }),

    getTaskTypes: build.query({
      query: (params = {}) => ({
        url: TASK_TYPES.LIST,
        method: 'GET',
        params,
      }),
      providesTags: (result) => listTags('TaskType', result),
    }),
    getTaskTypeById: build.query({
      query: (arg) => {
        const id = typeof arg === 'object' && arg != null ? arg.id : arg;
        const params =
          typeof arg === 'object' && arg != null ? arg.params : undefined;
        return { url: TASK_TYPES.BY_ID(id), method: 'GET', params };
      },
      providesTags: (_r, _e, arg) => [
        {
          type: 'TaskType',
          id: typeof arg === 'object' && arg != null ? arg.id : arg,
        },
      ],
    }),
    createTaskType: build.mutation({
      query: (arg) => {
        const isWrapped =
          arg &&
          typeof arg === 'object' &&
          !(arg instanceof FormData) &&
          ('data' in arg || 'params' in arg);
        const payload = isWrapped ? arg.data : arg;
        const params = isWrapped ? arg.params : undefined;
        return {
          url: TASK_TYPES.LIST,
          method: 'POST',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: [{ type: 'TaskType', id: 'LIST' }],
    }),
    updateTaskType: build.mutation({
      query: ({ id, data: updateData, params, ...rest }) => {
        const payload = updateData ?? rest;
        return {
          url: TASK_TYPES.BY_ID(id),
          method: 'PUT',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'TaskType', id },
        { type: 'TaskType', id: 'LIST' },
      ],
    }),
    deleteTaskType: build.mutation({
      query: (id) => ({ url: TASK_TYPES.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'TaskType', id },
        { type: 'TaskType', id: 'LIST' },
      ],
    }),

    getTaskCategories: build.query({
      query: (params = {}) => ({
        url: TASK_CATEGORIES.LIST,
        method: 'GET',
        params,
      }),
      providesTags: (result) => listTags('TaskCategory', result),
    }),
    getTaskCategoryById: build.query({
      query: (arg) => {
        const id = typeof arg === 'object' && arg != null ? arg.id : arg;
        const params =
          typeof arg === 'object' && arg != null ? arg.params : undefined;
        return { url: TASK_CATEGORIES.BY_ID(id), method: 'GET', params };
      },
      providesTags: (_r, _e, arg) => [
        {
          type: 'TaskCategory',
          id: typeof arg === 'object' && arg != null ? arg.id : arg,
        },
      ],
    }),
    createTaskCategory: build.mutation({
      query: (arg) => {
        const isWrapped =
          arg &&
          typeof arg === 'object' &&
          !(arg instanceof FormData) &&
          ('data' in arg || 'params' in arg);
        const payload = isWrapped ? arg.data : arg;
        const params = isWrapped ? arg.params : undefined;
        return {
          url: TASK_CATEGORIES.LIST,
          method: 'POST',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: [{ type: 'TaskCategory', id: 'LIST' }],
    }),
    updateTaskCategory: build.mutation({
      query: ({ id, data: updateData, params, ...rest }) => {
        const payload = updateData ?? rest;
        return {
          url: TASK_CATEGORIES.BY_ID(id),
          method: 'PUT',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'TaskCategory', id },
        { type: 'TaskCategory', id: 'LIST' },
      ],
    }),
    deleteTaskCategory: build.mutation({
      query: (id) => ({ url: TASK_CATEGORIES.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'TaskCategory', id },
        { type: 'TaskCategory', id: 'LIST' },
      ],
    }),

    getTaskTemplates: build.query({
      query: (params = {}) => ({
        url: TASK_TEMPLATES.LIST,
        method: 'GET',
        params,
      }),
      providesTags: (result) => listTags('TaskTemplate', result),
    }),
    getTaskTemplateById: build.query({
      query: (arg) => {
        const id = typeof arg === 'object' && arg != null ? arg.id : arg;
        const params =
          typeof arg === 'object' && arg != null ? arg.params : undefined;
        return { url: TASK_TEMPLATES.BY_ID(id), method: 'GET', params };
      },
      providesTags: (_r, _e, arg) => [
        {
          type: 'TaskTemplate',
          id: typeof arg === 'object' && arg != null ? arg.id : arg,
        },
      ],
    }),
    createTaskTemplate: build.mutation({
      query: (arg) => {
        const isWrapped =
          arg &&
          typeof arg === 'object' &&
          !(arg instanceof FormData) &&
          ('data' in arg || 'params' in arg);
        const payload = isWrapped ? arg.data : arg;
        const params = isWrapped ? arg.params : undefined;
        return {
          url: TASK_TEMPLATES.LIST,
          method: 'POST',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: [{ type: 'TaskTemplate', id: 'LIST' }],
    }),
    updateTaskTemplate: build.mutation({
      query: ({ id, data: updateData, params, ...rest }) => {
        const payload = updateData ?? rest;
        return {
          url: TASK_TEMPLATES.BY_ID(id),
          method: 'PUT',
          data: payload,
          params,
          ...withMultipart(payload),
        };
      },
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'TaskTemplate', id },
        { type: 'TaskTemplate', id: 'LIST' },
      ],
    }),
    deleteTaskTemplate: build.mutation({
      query: (id) => ({ url: TASK_TEMPLATES.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'TaskTemplate', id },
        { type: 'TaskTemplate', id: 'LIST' },
      ],
    }),

    getTaskComments: build.query({
      query: (params = {}) => ({
        url: TASK_COMMENTS.LIST,
        method: 'GET',
        params,
      }),
      providesTags: (result) => listTags('TaskComment', result),
    }),
    createTaskComment: build.mutation({
      query: (payload) => ({
        url: TASK_COMMENTS.LIST,
        method: 'POST',
        data: payload,
        ...withMultipart(payload),
      }),
      invalidatesTags: [{ type: 'TaskComment', id: 'LIST' }],
    }),
    deleteTaskComment: build.mutation({
      query: (id) => ({ url: TASK_COMMENTS.BY_ID(id), method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'TaskComment', id },
        { type: 'TaskComment', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useGetTasksQuery,
  useLazyGetTasksQuery,
  useGetTaskByIdQuery,
  useLazyGetTaskByIdQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
  useGetTaskTypesQuery,
  useLazyGetTaskTypesQuery,
  useGetTaskTypeByIdQuery,
  useLazyGetTaskTypeByIdQuery,
  useCreateTaskTypeMutation,
  useUpdateTaskTypeMutation,
  useDeleteTaskTypeMutation,
  useGetTaskCategoriesQuery,
  useLazyGetTaskCategoriesQuery,
  useGetTaskCategoryByIdQuery,
  useLazyGetTaskCategoryByIdQuery,
  useCreateTaskCategoryMutation,
  useUpdateTaskCategoryMutation,
  useDeleteTaskCategoryMutation,
  useGetTaskTemplatesQuery,
  useLazyGetTaskTemplatesQuery,
  useGetTaskTemplateByIdQuery,
  useLazyGetTaskTemplateByIdQuery,
  useCreateTaskTemplateMutation,
  useUpdateTaskTemplateMutation,
  useDeleteTaskTemplateMutation,
  useGetTaskCommentsQuery,
  useLazyGetTaskCommentsQuery,
  useCreateTaskCommentMutation,
  useDeleteTaskCommentMutation,
} = tasksApi;
